'use client';

/**
 * EntityDetailsLoader - Data fetching wrapper for schema-driven DetailsPanel
 *
 * Handles the different fetch patterns for each entity type:
 * - project: useProjectExplore → project
 * - rfq: useProjectExplore → project.rfqs.find(semantic_id)
 * - po: useProjectExplore → project.purchase_orders.find(semantic_id)
 * - vendor: useVendorExplore → vendor
 * - product: useProductExplore → product
 * - vendor_response: useVendorResponseExplore → response
 *
 * @component_role Data Loader
 * @business_rule Fetch entity data using appropriate API based on type
 * @layer presentation
 * @arch_component_type wrapper
 */

import {
  useProjectExplore,
  useVendorExplore,
  useProductExplore,
  useVendorResponseExplore,
  useStrategyData,
  useIntentSelectionCoherence,
} from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useIntentStore } from '@/lib/stores/intent-store';
import type {
  ProjectDetail,
  RFQDetail,
  PurchaseOrderDetail,
  VendorDetail,
  FullProductDetail,
  VendorResponseDetail,
} from '@/lib/api/types';
import { DetailsPanel } from './details-panel';

/**
 * Union type for all entity data structures.
 * Ensures type safety when extracting data from API responses.
 */
type EntityData =
  | (ProjectDetail & { rfq_count?: number; po_count?: number; total_vendors?: number })
  | (RFQDetail & { buyer?: { semantic_id: string; name: string } })
  | (PurchaseOrderDetail & { buyer?: { semantic_id: string; name: string } })
  | VendorDetail
  | FullProductDetail
  | VendorResponseDetail;

interface EntityDetailsLoaderProps {
  entityType: string;
  entityId: string;
  projectId?: string | null;
}

export function EntityDetailsLoader({
  entityType,
  entityId,
  projectId,
}: EntityDetailsLoaderProps) {
  // Resolve strategies from capability store
  const projectExploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const vendorExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_explore'));
  const productExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'product_explore'));
  const vendorResponseStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_response'));
  const designIntentStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'design_intent_explore'));
  const intentDetailStrategy = useCapabilityStore((s) => s.strategyName('intent', 'detail'));

  // Determine which hook to use based on entityType
  const needsProjectFetch =
    entityType === 'project' ||
    entityType === 'rfq' ||
    entityType === 'po';

  const needsVendorFetch = entityType === 'vendor';
  const needsProductFetch = entityType === 'product' || entityType === 'lineitem';
  const needsResponseFetch = entityType === 'vendor_response';
  const needsDesignIntentFetch = entityType === 'design_intent';
  const needsIntentFetch = entityType === 'intent';

  // ─────────────────────────────────────────────────────────────────────────────
  // Project/RFQ/PO fetching (all from project explore)
  // ─────────────────────────────────────────────────────────────────────────────
  const projectFetchId =
    entityType === 'project'
      ? entityId
      : projectId || null;

  const { data: projectData, isLoading: projectLoading } = useProjectExplore(
    projectExploreStrategy!,
    needsProjectFetch && projectFetchId
      ? {
          project_id: projectFetchId,
          include_relationships: ['rfqs', 'purchase_orders'],
          max_items: 50,
        }
      : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Vendor fetching
  // ─────────────────────────────────────────────────────────────────────────────
  const { data: vendorData, isLoading: vendorLoading } = useVendorExplore(
    vendorExploreStrategy!,
    needsVendorFetch
      ? { vendor_id: entityId, include_products: true }
      : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Product fetching
  // ─────────────────────────────────────────────────────────────────────────────
  const { data: productData, isLoading: productLoading } = useProductExplore(
    productExploreStrategy!,
    needsProductFetch
      ? { product_id: entityId }
      : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Vendor Response fetching
  // ─────────────────────────────────────────────────────────────────────────────
  const { data: responseData, isLoading: responseLoading } = useVendorResponseExplore(
    vendorResponseStrategy!,
    needsResponseFetch
      ? { response_cdk: entityId }
      : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Design Intent fetching — returns DesignIntentExploreResponse shape via
  // generic useStrategyData. DetailsPanel consumes the `design_intent` field
  // as Record<string, unknown> the same way product/vendor flows do.
  // ─────────────────────────────────────────────────────────────────────────────
  const { data: designIntentData, isLoading: designIntentLoading } = useStrategyData<{
    design_intent: Record<string, unknown> | null;
    metadata: Record<string, unknown>;
  }>(
    designIntentStrategy,
    needsDesignIntentFetch ? { semantic_id: entityId } : {},
    needsDesignIntentFetch && !!designIntentStrategy,
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Intent fetching — intent_details returns IntentListResponse shape
  // (items: [IntentSummary], total). Extract .items[0].
  // ─────────────────────────────────────────────────────────────────────────────
  // Fallback to the hardcoded strategy name because intent strategies
  // aren't yet synced to the BusinessEntity graph registry the
  // capability store reads — backend dispatches fine either way.
  // Remove the ?? once `bff_strategies` sync covers intent.*.
  const intentStrategyResolved = intentDetailStrategy ?? 'intent_details';
  const { data: intentData, isLoading: intentLoading } = useStrategyData<{
    items: Record<string, unknown>[];
    total: number;
  }>(
    intentStrategyResolved,
    needsIntentFetch ? { intent_sid: entityId } : {},
    needsIntentFetch,
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Selection coherence — live-scored from the buyer's current picks
  // across every lane on this Intent. Never persisted; recomputed on each
  // pick change. `lineSelections` is a flat `{line_sid: product_sid}` map
  // maintained by the grid widget on card click.
  // ─────────────────────────────────────────────────────────────────────────────
  const lineSelections = useIntentStore((s) => s.lineSelections);
  const selectionList = needsIntentFetch
    ? Object.entries(lineSelections)
        .filter(([, product_sid]) => !!product_sid)
        .map(([line_sid, product_sid]) => ({ line_sid, product_sid }))
    : [];
  const { data: selectionCoherence } = useIntentSelectionCoherence(
    'intent_selection_coherence',
    needsIntentFetch
      ? { intent_sid: entityId, selections: selectionList }
      : null,
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Extract and normalize data based on entityType
  // ─────────────────────────────────────────────────────────────────────────────
  const isLoading =
    (needsProjectFetch && projectLoading) ||
    (needsVendorFetch && vendorLoading) ||
    (needsProductFetch && productLoading) ||
    (needsResponseFetch && responseLoading) ||
    (needsDesignIntentFetch && designIntentLoading) ||
    (needsIntentFetch && intentLoading);

  let data: EntityData | null = null;

  switch (entityType) {
    case 'project':
      if (projectData?.project) {
        data = {
          ...projectData.project,
          // Add metadata fields at top level for schema access
          rfq_count: projectData.metadata?.rfq_count,
          po_count: projectData.metadata?.po_count,
          total_vendors: projectData.metadata?.total_vendors,
        };
      }
      break;

    case 'rfq':
      if (projectData?.project) {
        const rfq = projectData.project.rfqs?.find(
          (r) => r.semantic_id === entityId
        );
        if (rfq) {
          data = {
            ...rfq,
            // Add parent project customer as buyer
            buyer: projectData.project.customer,
          };
        }
      }
      break;

    case 'po':
      if (projectData?.project) {
        const po = projectData.project.purchase_orders?.find(
          (p) => p.semantic_id === entityId
        );
        if (po) {
          data = {
            ...po,
            buyer: projectData.project.customer,
          };
        }
      }
      break;

    case 'vendor':
      if (vendorData?.vendor) {
        data = vendorData.vendor;
      }
      break;

    case 'product':
    case 'lineitem':
      if (productData?.product) {
        data = productData.product;
      }
      break;

    case 'vendor_response':
      if (responseData?.response) {
        data = responseData.response;
      }
      break;

    case 'design_intent': {
      const payload = designIntentData?.data?.design_intent;
      if (payload) {
        data = payload as unknown as EntityData;
      }
      break;
    }

    case 'intent': {
      // intent_details returns { items: [IntentSummary], total }.
      // Schema (ENTITY_SCHEMA['intent']) reads the fields on .items[0]:
      // media_urls, room_label, designer_brief, intent_description,
      // style, mood, primary_color, primary_material, intent_sid.
      // selection_coherence_pct is overlaid from the live BFF call so
      // the stats section shows brief vs picks side-by-side.
      const payload = intentData?.data?.items?.[0];
      if (payload) {
        data = {
          ...payload,
          selection_coherence_pct: selectionCoherence?.selection_coherence_pct ?? null,
          selection_pick_count: selectionCoherence?.pick_count ?? 0,
          selection_worst_pick: selectionCoherence?.worst_pick ?? null,
        } as unknown as EntityData;
      }
      break;
    }
  }

  // DetailsPanel uses dynamic field access via ENTITY_SCHEMA,
  // so we pass the typed data as Record<string, unknown>
  return (
    <DetailsPanel
      entityType={entityType}
      entityId={entityId}
      data={(data as unknown as Record<string, unknown>) || {}}
      isLoading={isLoading}
    />
  );
}
